<template>
  <div v-if="isVisible" class="spoiler-section">
    <SrpDetailsSummary class="spoiler-section__details-summary" v-model:is-opened="isOpened" :transition-duration="150">
      <template #heading="{ isOpened }">
        <div
          :class="{
            'global-h3': true,
            'spoiler-section__summary': true,
            'spoiler-section__summary--with-bottom-padding': !isOpened,
          }"
        >
          <span
            ><span>
              {{ collabComplete ? "Creator Visit Complete!" : "Content Ready!" }}
            </span></span
          >
        </div>
      </template>
      <template #details>
        <div class="spoiler-section__content">
          <template v-if="!collabComplete">
            <NoteWithIcon style="margin-bottom: 20px">
              <template #text>
                <div>
                  <template v-if="daysToProvideCorrections - countDaysFromDateToNow(collabInput.collaboration.notifications.firstApproveNotification) > 0">
                    <h5 class="global-h5" style="margin-bottom: 4px; display: inline">
                      In the next {{ daysToProvideCorrections - countDaysFromDateToNow(collabInput.collaboration.notifications.firstApproveNotification) }} days
                    </h5>
                    please review and request any corrections needed. Thanks!
                  </template>
                  <template v-else>
                    <h5 class="global-h5" style="display: inline">As soon as possible</h5>
                    please review and request any corrections needed. Thanks!
                  </template>
                </div>
              </template>
            </NoteWithIcon>

            <div class="spoiler-section__content-divider" style="margin-bottom: 20px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>

          <StatNumbersBanner
            style="margin-bottom: 35px"
            :fields="{
              Locations: customerSummary?.locationCount,
              Photos: customerSummary?.photoCount,
              Videos: customerSummary?.videoCount,
            }"
          />

          <div class="spoiler-section__content-divider" style="margin-bottom: 30px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>

          <template v-if="customerSummary?.mediaIds?.length">
            <!-- Section header -->
            <div class="section-header" style="margin-bottom: 12px">
              <h4 class="global-h4 section-header__title">{{ showLocations ? "Highlights" : "Photos &amp; Videos" }}</h4>

              <div style="gap: 25px; display: flex; align-items: center">
                <LinkWithIcon
                  v-if="collabInput.collaboration.completedDate"
                  class="section-header__link"
                  isDottedUnderline
                  @click="requestDownloadAllMediaFiles"
                  :isDisabled="isDownloadRequestInProgress"
                >
                  <template #icon>
                    <LoadingIcon v-if="isDownloadRequestInProgress" :size="17" />
                    <IconEmbedded v-else name="download_2" :size="20" />
                  </template>
                  <span>Download all</span>
                </LinkWithIcon>

                <CompilingZIPMsgModal
                  v-if="isDownloadRequestInfoModalVisible"
                  v-model:isVisible="isDownloadRequestInfoModalVisible"
                  :customerId="communityId"
                  :contextId="collabInput.id"
                  :collabInputId="collabInput.id"
                  :uploadCampaignId="null"
                  :uploaderEmail="null"
                  :locationExternalId="null"
                />

                <LinkWithIcon
                  v-if="collabInput.collaboration.completedDate"
                  class="section-header__link"
                  :to="{
                    name: 'CommunityPhotos',
                    params: { pageId: communityId },
                  }"
                  tag="RouterLink"
                >
                  <template #icon><IconEmbedded name="image_2" :size="20" /></template>
                  <span>All photos</span>
                </LinkWithIcon>
              </div>
            </div>
            <!-- / Section header -->

            <!-- Highlights section wrap -->
            <div
              :class="{
                'highlights-section-wrap': true,
                'highlights-section-wrap--with-curtain': !isHighlightsExpanded && customerSummary?.mediaIds?.length > numberOfThumbnailsToShowWithoutCollapsing,
              }"
              style="margin-bottom: 30px"
            >
              <Component
                :is="customerSummary?.mediaIds?.length > numberOfThumbnailsToShowWithoutCollapsing ? 'TransitionedHeightAuto' : 'div'"
                class="highlights-section-wrap__transitioned-height-wrap"
                :minHeight="screenSize === 'mobile' ? 230 : 350"
                :isOpened="isHighlightsExpanded"
                isKeepAlive
                :transitionDuration="150"
              >
                <HighlightsSection
                  class="highlights-section-wrap__highlights-section"
                  :highlightAssetIds="showLocations ? customerSummary?.highlights : customerSummary?.mediaIds"
                  :visibleLines="100"
                  :stretchToFill="!showLocations"
                />
              </Component>

              <LinkWithIcon
                v-if="customerSummary?.mediaIds?.length > numberOfThumbnailsToShowWithoutCollapsing"
                class="highlights-section-wrap__show-more-btn"
                isDottedUnderline
                iconInset="1px auto auto 2px"
                @click="isHighlightsExpanded = !isHighlightsExpanded"
              >
                <template #icon><IconEmbedded :name="isHighlightsExpanded ? 'caret-top_4' : 'caret-bottom_4'" :size="14" /></template>
                <span>{{ isHighlightsExpanded ? "Hide" : "Show more" }}</span>
              </LinkWithIcon>
            </div>
            <!-- / Highlights section wrap -->

            <div style="margin-bottom: 21px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>

          <!-- Locations Section -->
          <template v-if="showLocations && customerSummary?.locations?.length">
            <h2 class="global-h2" style="margin-top: 50px; margin-bottom: 28px; text-align: center">Locations Visited</h2>
            <div style="margin-bottom: 30px">
              <LocationSummary v-for="location in customerSummary.locations" :key="location.title" :location="location" style="margin-bottom: 45px" />
            </div>
            <div style="margin-bottom: 21px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>
          <!-- / Locations Section -->

          <template v-if="!showLocations && customerSummary?.adventures.length > 0">
            <h4 class="global-h4" style="margin-bottom: 12px">
              <span v-if="!Boolean(collabInput.collaboration.completedDate)">Adventure Review &amp; Edits</span>
              <span v-else>Adventures</span>
            </h4>
            <div v-if="customerSummary?.adventures.length > 0" style="padding-bottom: 50px">
              <ul style="width: 100%; padding: 0; margin: 0; display: flex; flex-wrap: wrap; list-style: none">
                <ItineraryDetailsSnippet
                  class=""
                  v-for="(itinerary, index) in customerSummary?.adventures"
                  :key="itinerary.id"
                  :itinerary="itinerary"
                  :style="{ marginBottom: index !== customerSummary?.adventures.length - 1 ? '25px' : 0 }"
                  target="_blank"
                  :enableStopDetailsSection="true"
                  :isEditingAllowed="!Boolean(collabInput.collaboration.completedDate)"
                  :defaultStopsOpen="!Boolean(collabInput.collaboration.completedDate)"
                />
              </ul>
            </div>
          </template>

          <template v-if="customerSummary?.socialPostLink">
            <div style="margin: 30px 0 30px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>

            <h4 class="global-h4" style="margin-bottom: 5px">Creator's Social Post</h4>
            <div style="margin-bottom: 33px; display: flex; align-items: center">
              <div class="global-a" v-html="linkifyStr(customerSummary?.socialPostLink, globalRoot.linkifyOptions)"></div>
              <IconEmbedded name="external-link_2-5" :size="16" style="margin-left: 3px; opacity: 0.5" color="brandColor" />
            </div>

            <div style="margin-bottom: 20px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>

          <div class="floating-buttons-panel">
            <div v-if="!showCompleteSection && !showCorrectionsSection" style="margin-bottom: 10px">
              <label v-if="correctionsWereMade" class="cms-section-title" style="margin-bottom: 12px !important">The creator made the corrections, how do they look?</label>
              <label v-else class="cms-section-title" style="margin-bottom: 17px !important">How does everything look?</label>
              <button :disabled="isSaving" class="ui primary button" @click="looksGoodClick()" style="margin: 0 50px 12px 0">Looks good!</button>
              <button class="ui primary button" @click="showCorrectionsSection = true">I'd like to request {{ correctionsWereMade ? "further" : "" }} corrections</button>
            </div>
            <!-- Old simple Content Uses section -->
            <div v-if="showCompleteSection && !collabInput?.collaboration?.customerOwnedAdventures">
              <div style="font-size: 1.2em">
                <div v-if="!collabComplete">Awesome! We've marked this Creator Visit as complete.</div>
                <h4 class="global-h4" style="margin-bottom: 14px">Let's make the most of these!</h4>

                <ul style="margin: 0 0 25px; padding: 0; list-style: none">
                  <li style="margin-bottom: 5px">
                    - Share them on
                    <RouterLink class="global-a" :to="{ name: 'CommunityAdminDashboard', params: { pageId: communityId } }" style="font-size: 16.8px; text-decoration: underline">
                      Social Media!</RouterLink
                    >
                  </li>
                  <li style="margin-bottom: 5px">
                    - Add the adventures to your website with
                    <RouterLink class="global-a" :to="{ name: 'AdventureWidgets', params: { pageId: communityId } }" style="font-size: 16.8px; text-decoration: underline">
                      Website Integration</RouterLink
                    >
                  </li>
                  <li
                    :style="{
                      marginBottom: '5px',
                      opacity: isDownloadRequestInProgress ? 0.5 : 1,
                      pointerEvents: isDownloadRequestInProgress ? 'none' : 'auto',
                    }"
                  >
                    -
                    <a class="global-a" @click="requestDownloadAllMediaFiles" style="font-size: 16.8px">
                      <LoadingIcon v-if="isDownloadRequestInProgress" :size="17" />
                      Download all photos & videos
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <!--Corrections-->
            <div v-if="showCorrectionsSection">
              <label class="cms-section-title" style="display: inline">Request Creator Corrections?</label>
              <span style="float: right; margin-top: 8px" @click="showCorrectionsSection = false">
                <div name="angle left" class="large angle left" />
              </span>
              <div>
                Give creative license when possible and be kind!
                <textarea maxlength="3000" v-model="collabInput.correctionsForCreator" :placeholder="textareaPlaceholder" style="height: 70px; min-height: 70px"></textarea>
                <div v-if="showRequired" style="color: red; font-weight: bold">Corrections Required</div>
                <div v-if="needsCommunityReview" style="margin-top: 10px">
                  <button :disabled="isSaving" @click="requestCorrections()" class="ui primary button">That's everything, request the corrections!</button>
                  <h4 v-if="isSaving" style="display: inline">&nbsp;Saving...</h4>
                </div>
                <div v-else style="margin-top: 20px; margin-bottom: 20px; font-size: 1.3em">Thanks! We've notified the creator and you should expect the updates soon.</div>
              </div>
            </div>
          </div>

          <div>Questions or issues? Feel free to email us at <CopyText :text="'collabs@shrpa.com'" /></div>
        </div>
      </template>
    </SrpDetailsSummary>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import axios from "axios";
import CollabStatuses from "@logic/CollabStatuses";
import CollabStateHelper from "@logic/CollabStateHelper";
import FeatureFlags from "@logic/FeatureFlags";
import FileUtils from "@logic/FileUtils";
import linkifyStr from "linkify-string";
import AssetsDownloadRequestRepo from "@repos/assetsDownloadRequestRepo";
import FileDownload from "@logic/FileDownload";

// Types
import { CollabInput } from "@contracts/collab";
import { Itinerary } from "@contracts/itinerary";
import { ScreenSize } from "@contracts/screenSize";
import { CollabAdventureLocationSummary } from "@contracts/collabLocations";

interface CustomerSummary {
  locationCount: number;
  photoCount: number;
  videoCount: number;

  mediaIds: Array<string>;
  highlights: Array<string>;
  adventures: Array<Itinerary>;
  locations: Array<CollabAdventureLocationSummary>;

  socialPostLink: string | null;
}

// Components
import CopyText from "@components/CopyText.vue";
import HighlightsSection from "@components/HighlightsSection.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import ItineraryDetailsSnippet from "@components/ItineraryDetailsSnippet.vue";
import ItineraryTile from "@components/ItineraryTile.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LocationSnippet from "./LocationSnippet.vue";
import LocationSummary from "./LocationSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import StatNumbersBanner from "@components/StatNumbersBanner.vue";
import TransitionedHeightAuto from "@components/ui/TransitionedHeightAuto.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import CompilingZIPMsgModal from "@components/Modals/CompilingZIPMsgModal.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Images
import makeTheMostImage1 from "@assets/images/make-the-most-image-1.jpg";
import makeTheMostImage2 from "@assets/images/make-the-most-image-2.jpg";
import makeTheMostImage3 from "@assets/images/make-the-most-image-3.jpg";

export default defineComponent({
  name: "CommunityCorrections",

  components: {
    LoadingIcon,
    CompilingZIPMsgModal,
    CopyText,
    HighlightsSection,
    IconEmbedded,
    ItineraryDetailsSnippet,
    ItineraryTile,
    LinkWithIcon,
    LocationSnippet,
    LocationSummary,
    SrpButton,
    SrpDetailsSummary,
    StatNumbersBanner,
    TransitionedHeightAuto,
    NoteWithIcon,
  },

  props: {
    communityId: { type: String, required: true },
    collabInput: { type: Object as () => CollabInput, required: true },
    collabIndex: { type: Number, required: true },
    creatorId: { type: String, required: true },
    forceLocationsView: { type: Boolean, default: false },
  },

  emits: ["updated", "collabMarkedComplete"],

  data() {
    return {
      globalLog: inject("globalLog") as any,
      screenSize: inject("screenSize") as ScreenSize,

      daysToProvideCorrections: 10, // NOTE: Configured on the server also
      isSaving: false,
      showRequired: false,
      isOpened: true,

      showCorrectionsSection: false,
      showCompleteSection: false,

      textareaPlaceholder: "- Is anything missing or inaccurate?\n" + "- Other changes needed?",

      isHighlightsExpanded: false,

      FileUtils,
      FeatureFlags,

      customerSummary: null as CustomerSummary,

      isDownloadRequestInProgress: false,
      isDownloadRequestInfoModalVisible: false,

      images: {
        makeTheMostImage1,
        makeTheMostImage2,
        makeTheMostImage3,
      },

      numberOfThumbnailsToShowWithoutCollapsing: 12,
    };
  },

  computed: {
    collabComplete(): boolean {
      return Boolean(this.collabInput.collaboration.completedDate);
    },
    needsCommunityReview(): boolean {
      return this.collabInput.correctionsStatus === CollabStatuses.CorrectionStatusCommunityReview;
    },
    isVisible(): boolean {
      return CollabStateHelper.canShowCorrectionsUI(this.collabInput);
    },
    correctionsWereMade(): boolean {
      // Corrections status (see CollabStatuses.ts)
      return this.collabInput.collaboration.status === CollabStatuses.CreatorCorrectionsMade;
    },
    showLocations(): boolean {
      return this.collabInput?.collaboration?.customerOwnedAdventures || this.forceLocationsView;
    },
  },

  watch: {
    // Resets the state since they changed collabs
    collabIndex: function (newVal, oldVal) {
      this.globalLog.info(`collabIndex changed: new=${newVal}, old=${oldVal}`);
      this.onLoad();
    },
    collabInput: {
      handler(_, oldVal) {
        if (oldVal != null) {
          this.globalLog.debug("Emitting update for Collab");
          this.$emit("updated", this.collabInput, this.collabIndex);
        }
      },
      deep: true,
    },
  },

  async mounted() {
    await this.onLoad();
  },

  methods: {
    linkifyStr,
    async requestDownloadAllMediaFiles() {
      this.isDownloadRequestInProgress = true;

      const downloadRequest = await AssetsDownloadRequestRepo.LoadByContextId(this.communityId, this.collabInput.id, true, this.collabInput.id, null, null);

      if (downloadRequest.completedDate) {
        FileDownload.downloadFileByURI(`${globalThis.Bootstrap.Config.contentBlobBaseUri}/${downloadRequest.downloadFileLocation}`);
      } else {
        this.isDownloadRequestInfoModalVisible = true;
      }

      this.isDownloadRequestInProgress = false;
    },
    async onLoad() {
      if (this.collabInput.collaboration.completedDate) {
        this.showCorrectionsSection = false;
        this.showCompleteSection = true;
      } else if (
        this.collabInput.correctionsForCreator &&
        this.collabInput.correctionsForCreator.length > 0 &&
        // NOTE: Use the status to check if corrections have been made and re-ask them if things look good.
        this.collabInput.collaboration.status !== "105"
      ) {
        this.showCorrectionsSection = true;
      } else {
        // Reset the variables back to their defaults
        this.showCompleteSection = false;
        this.showCorrectionsSection = false;
      }

      this.customerSummary = await this.loadCustomerSummary();
    },
    async loadCustomerSummary(): Promise<CustomerSummary> {
      // Note: Passed the status field for the old flow (before customer owned adventures)
      let uri = `${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${this.collabInput.id}/customer-summary?status=CollabCommunityReview`;
      const { data } = await axios.get<CustomerSummary>(uri);

      return data;
    },
    countDaysFromDateToNow(dateToStartFrom: string): number {
      if (!dateToStartFrom) {
        return 0;
      }
      const pastDate: Date = new Date(dateToStartFrom);
      const currentDate: Date = new Date();

      const diffInMilliseconds: number = currentDate.getTime() - pastDate.getTime();

      return Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    },
    async requestCorrections() {
      if (!this.validate()) {
        return;
      }
      this.isSaving = true;
      const config = { headers: { "Content-Type": "application/json" } };
      await axios.put(`${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${this.collabInput.id}/correctionsRequest`, JSON.stringify(this.collabInput.correctionsForCreator), config);
      this.isSaving = false;
      // Update the status
      this.collabInput.correctionsStatus = "PendingCreator";
    },
    async looksGoodClick() {
      this.isSaving = true;
      var response = await axios.put(`${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${this.collabInput.id}/communityapproved`);
      this.isSaving = false;
      // Mark complete
      this.collabInput.collaboration.completedDate = response.data;
      this.showCompleteSection = true;
      this.$emit("collabMarkedComplete");
    },
    validate(): boolean {
      if (!this.collabInput.correctionsForCreator || this.collabInput.correctionsForCreator.length == 0) {
        this.showRequired = true;
        return false;
      }
      // All good
      this.showRequired = false;
      return true;
    },
  },
});
</script>

<style scoped lang="scss">
@import "../AllCollabsV2.scss";
@import "@/scss/screen-size-ranges.scss";

// Section header =============================================================
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__title {
  }

  &__link {
  }
}

.floating-buttons-panel {
  //margin-bottom: 20px;
  border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  position: sticky;
  inset: auto auto 0 0;
  z-index: 50;

  &::before {
    content: "";
    width: 100%;
    height: calc(100% + 50px);
    position: absolute;
    inset: auto auto 0 0;
    z-index: -1;
    background: linear-gradient(0deg, rgba(255, 255, 255, 1) 70%, rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
  }
}

// Adventure tiles list =======================================================
.adv-tiles-list {
  width: 100%;
  gap: 20px;
  padding: 0 0 40px;
  margin: 0;
  border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  display: flex;
  flex-wrap: wrap;
  list-style: none;

  &__li {
    width: calc(100% / 4 - 16px);
    margin: 0;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .adv-tiles-list {
    &__li {
      width: calc(100% / 4 - 15px);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .adv-tiles-list {
    &__li {
      width: calc(100% / 3 - 14px);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .adv-tiles-list {
    gap: 15px;

    &__li {
      width: calc(100% / 2 - 10px);
    }
  }
}

// Tiles section ==============================================================
.tiles-section {
  width: var(--grid-col-right-width);
  padding: 0;
  margin: 0;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: $desktop-wide-grid-gap-width;
  list-style: none;

  &__tile {
    width: calc(100% / 3 - $desktop-wide-grid-gap-width * 2 / 3);
    max-width: calc(100% / 3 - $desktop-wide-grid-gap-width * 2 / 3);
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .tiles-section {
    width: var(--grid-content-full-width);
    gap: $tablet-large-grid-gap-width;

    &__tile {
      width: calc(100% / 3 - $tablet-large-grid-gap-width * 2 / 3);
      max-width: calc(100% / 3 - $tablet-large-grid-gap-width * 2 / 3);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .tiles-section {
    width: var(--grid-content-full-width);
    gap: $tablet-grid-gap-width;

    &__tile {
      width: calc(100% / 2 - $tablet-grid-gap-width / 2);
      max-width: calc(100% / 2 - $tablet-grid-gap-width / 2);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .tiles-section {
    width: var(--grid-content-full-width);
    gap: $mobile-grid-gap-width;

    &__tile {
      width: calc(100% / 2 - $mobile-grid-gap-width / 2);
      max-width: calc(100% / 2 - $mobile-grid-gap-width / 2);
    }
  }
}

// Highlights section wrap ====================================================
.highlights-section-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: 100%;
    height: 200px;
    position: absolute;
    inset: auto auto 0 0;
    z-index: 2;
    background: linear-gradient(0deg, rgba(255, 255, 255, 1) 20%, rgba(255, 255, 255, 0) 100%);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease-in-out;
  }

  &--with-curtain {
    &::before {
      opacity: 1;
    }
  }

  &__transitioned-height-wrap {
    position: relative;
    z-index: 1;
  }

  &__highlights-section {
  }

  &__show-more-btn {
    position: relative;
    z-index: 3;
  }
}

.ui.form {
  padding: 20px;
  box-shadow: 0 0 5px 0 #ccc;
  .cms-section-title {
    margin: 25px 0 10px 0 !important;
  }
  .btn-groups {
    text-align: right;
  }

  .adventure-heading {
    display: inline;
  }
}
@media screen and (max-width: 420px) {
  .ui.form {
    padding: 15px;
    h1 {
      font-size: 18px !important;
    }
  }
}
</style>
